import { Wallet } from "lucide-react";
import { ManualeLayout, Scheda, P, Steps, Bullets, Callout, FAQ } from "@/pages/manuali/ManualeLayout";

const A = "#0d9488";
const SEZIONI = [
  { id: "intro", titolo: "A cosa serve" },
  { id: "portafoglio", titolo: "Portafoglio titoli" },
  { id: "import", titolo: "Import titoli da Assieasy" },
  { id: "incasso", titolo: "Incassare un titolo" },
  { id: "casse", titolo: "Casse & Conti" },
  { id: "primanota", titolo: "Prima Nota Cassa" },
  { id: "crediti", titolo: "Crediti da recuperare" },
  { id: "chiusura", titolo: "Chiusura cassa" },
  { id: "report", titolo: "Report e solleciti" },
  { id: "operativita", titolo: "Operatività avanzata" },
  { id: "riconciliazione", titolo: "Riconciliazione bancaria" },
  { id: "faq", titolo: "FAQ e avvertenze" },
];

export default function ManualeContabilita() {
  return (
    <ManualeLayout titolo="Contabilità di Agenzia" sottotitolo="Portafoglio titoli, casse, incassi, chiusure e riconciliazione" icona={Wallet} accent={A} sezioni={SEZIONI}>
      <Scheda id="intro" n="1" titolo="A cosa serve" accent={A}>
        <P>Il modulo gestisce l'intero ciclo dei <b>titoli</b> (rate di premio da incassare): dall'inserimento all'incasso, con instradamento automatico del denaro nelle <b>casse</b> corrette, gestione di <b>crediti/anticipi</b>, <b>chiusura cassa</b> giornaliera, <b>report</b> e <b>riconciliazione bancaria</b>.</P>
        <Callout tipo="info">In alto trovi 4 indicatori sempre aggiornati: Da incassare (oggi/settimana/mese), Arretrati, Incassato del mese, Crediti da recuperare.</Callout>
      </Scheda>

      <Scheda id="portafoglio" n="2" titolo="Portafoglio titoli" accent={A}>
        <P><b>Creare un titolo:</b> pulsante <b>“Nuovo titolo”</b> in alto a destra.</P>
        <Steps items={[
          "Seleziona il Cliente (ricerca in anagrafica) e inserisci N° polizza e Compagnia.",
          "Indica Ramo, Intermediario, Premio lordo (obbligatorio) ed eventuale Premio netto da volturare.",
          "Imposta Frazionamento, Data effetto e Data scadenza, quindi Salva.",
        ]} />
        <P><b>Stati del titolo:</b></P>
        <Bullets items={[
          "Da incassare · Incassato · Arretrato (scaduto e non incassato) · Anticipato (pagato dall'agenzia/collaboratore) · Stornato.",
          "Usa i filtri (Stato, Compagnia, Intermediario) e la ricerca per trovare rapidamente un titolo.",
        ]} />
        <P><b>Azioni sulla riga:</b> Incassa (se Da incassare/Arretrato), Storna (annulla un incasso), Modifica, Elimina. Con i pulsanti <b>Excel</b>/<b>PDF</b> esporti l'elenco filtrato.</P>
      </Scheda>

      <Scheda id="import" n="3" titolo="Import titoli da Assieasy" accent={A}>
        <P>Il quietanzamento si carica dal pulsante <b>“Importa da Assieasy”</b> (in Portafoglio titoli).</P>
        <Steps items={[
          "Esporta da Assieasy l'elenco titoli/avvisi (Quietanzamenti) in formato CSV.",
          "Premi “Importa da Assieasy” e seleziona il file: i titoli entrano come Da incassare.",
          "A fine import vedi il riepilogo (nuovi, saltati, totale, per compagnia).",
        ]} />
        <Bullets items={[
          "Import idempotente: ricaricando lo stesso file le righe già presenti vengono scartate (niente doppioni).",
          "Legge automaticamente i vari tracciati Assieasy: premio (Premio Titolo/Premio Lordo) e produttore (Subagente Titolo, ripulito dal codice, es. “0003 DESSI MARINA” → “DESSI MARINA”).",
          "Sbagliato file? Usa “Annulla import” per rimuovere l'ultimo caricamento (i titoli già incassati non vengono toccati).",
        ]} />
        <Callout tipo="info"><b>Autorizzare i colleghi:</b> di norma l'import è riservato alla Direzione. Con <b>“Autorizza import”</b> (visibile solo alla Direzione) puoi abilitare altri colleghi dell'amministrazione (es. Melania) aggiungendo la loro email; potranno usare Importa/Annulla import.</Callout>
      </Scheda>

      <Scheda id="incasso" n="4" titolo="Incassare un titolo" accent={A}>
        <P>Premi <b>“Incassa”</b> sulla riga: scegli la <b>Modalità di pagamento</b> — il denaro viene instradato automaticamente nel registro giusto (<b>catena unica</b>, senza doppio conteggio).</P>
        <Bullets items={[
          "Contante / Assegno / Bonifico / POS → crea UN movimento nel Foglio Cassa del produttore e confluisce automaticamente in Prima Nota Cassa.",
          "Foglio Cassa (produttore): è precompilato in automatico dall'intermediario del titolo (es. “DESSI MARINA” → Marina) e resta modificabile con la tendina; se non c'è corrispondenza va su “Agenzia”.",
          "Bonifico/POS: registrati ma esclusi dalla rimessa (sono già in banca).",
          "Finanziamenti (Compass/Agos) / Pagato a Compagnia → conto extra-cassa dedicato (restano nella Contabilità, non toccano la cassa fisica).",
          "Anticipo cliente/collaboratore → non entra denaro: si crea un Credito e il titolo diventa Anticipato.",
        ]} />
        <Callout tipo="warn"><b>Blocco doppio incasso:</b> se la polizza risulta già in un Foglio Cassa, un avviso te lo segnala e chiede conferma prima di registrarla di nuovo.</Callout>
        <Callout tipo="ok">Sbagliato qualcosa? Usa <b>Storna</b> sulla riga: annulla l'incasso e rimuove anche i movimenti creati in Foglio Cassa e Prima Nota.</Callout>
      </Scheda>

      <Scheda id="casse" n="5" titolo="Casse & Conti" accent={A}>
        <P>In cima trovi il <b>Riepilogo Cassa Agenzia (dalla Prima Nota)</b> — fonte unica: Saldo, Entrate, Da versare (contanti+assegni) e Sospesi aperti, con link diretto <b>“Apri Prima Nota Cassa”</b>. Sotto restano i conti <b>extra-cassa</b> (finanziamenti, incasso diretto compagnia, storico).</P>
        <Bullets items={[
          "Chiudi sospeso Compass / Agos: seleziona i titoli del lotto liquidato dalla finanziaria e registra il bonifico cumulativo ricevuto.",
          "La cassa fisica (contanti, assegni, versamenti) si gestisce ora in Prima Nota Cassa.",
        ]} />
      </Scheda>

      <Scheda id="primanota" n="6" titolo="Prima Nota Cassa" accent={A}>
        <P>È il <b>registro unico della cassa dell'Agenzia</b> (route <b>/prima-nota</b>). Raccoglie i movimenti, i versamenti e i sospesi, e include la riconciliazione con i fogli cassa delle compagnie.</P>
        <P><b>Movimenti:</b> ricerca, filtri e ordinamento rapido (Data/Cliente/Intermediario). Sulle righe puoi segnare <b>Consegnato in agenzia</b> e <b>Versato in banca</b> scegliendo la <b>data effettiva</b> (calendario).</P>
        <P><b>Stati operativi (sospesi):</b> ogni movimento può essere Sospeso, Sospeso parziale (con residuo), Sospeso Cofidis/Compass, In transito (subagente), Bonifico da verificare. Il <b>cruscotto sospesi</b> in alto mostra importi per tipo; i sospesi aperti da più giorni della soglia sono evidenziati in rosso.</P>
        <Bullets items={[
          "Chiudere un sospeso: solo Direzione + colleghi autorizzati (da “Impostazioni sospesi”). Ogni chiusura è tracciata (Storico chiusure).",
          "Sospeso Cofidis/Compass: si chiude in automatico quando registri il bonifico ricevuto (versato in banca).",
          "Distinta versamento (PDF): separa Contanti e Assegni pronti per la banca.",
          "Report giornaliero (PDF): riepilogo di fine giornata per modalità e produttore, da stampare per l'ufficio.",
          "Report sospesi (PDF): elenco dei sospesi aperti per tipo, per la riunione settimanale.",
        ]} />
        <P><b>Riconciliazione Compagnie</b> (sotto-tab): carichi il Foglio Cassa della compagnia (Excel Generali; PDF Assimoco, Groupama, Essellepi/SLP) e confronti con la Prima Nota. Il periodo è precompilato dalle date del foglio (modificabile).</P>
        <Bullets items={[
          "MATCH = presente sia nel foglio compagnia sia incassato in Prima Nota.",
          "ERRORE = incassato in Prima Nota ma assente nel foglio compagnia (confrontato solo nel periodo indicato).",
          "ALLARME = presente nel foglio compagnia ma assente in Prima Nota. Spuntando “Crea sospesi automatici” genera i relativi sospesi.",
        ]} />
      </Scheda>


      <Scheda id="crediti" n="5" titolo="Crediti da recuperare" accent={A}>
        <P>Elenca gli <b>anticipi</b> aperti (verso cliente o del collaboratore). Con <b>“Recupera”</b> registri il rientro:</P>
        <Steps items={[
          "Scegli la modalità: Bonifico → Banca oppure Contante → Cassa.",
          "Per bonifico inserisci il riferimento; conferma: l'importo entra in cassa e il titolo torna Incassato.",
        ]} />
      </Scheda>

      <Scheda id="chiusura" n="6" titolo="Chiusura cassa" accent={A}>
        <P>Serve a <b>quadrare il reale con l'atteso</b> a fine giornata.</P>
        <Steps items={[
          "Per ogni cassa inserisci l'importo effettivamente contato.",
          "Se c'è uno scostamento, il sistema lo evidenzia e richiede una Nota di Rettifica (obbligatoria).",
          "Premi “Registra chiusura”: la chiusura viene salvata nello storico.",
        ]} />
        <Callout tipo="warn">Le rettifiche restano tracciate nello <b>Storico chiusure</b> per controllo e audit.</Callout>
      </Scheda>

      <Scheda id="report" n="7" titolo="Report e solleciti" accent={A}>
        <Bullets items={[
          "Volume per canale di incasso (grafico a torta) e Premi per compagnia (incassato vs arretrato).",
          "Arretrati ordinati per gravità (giorni di ritardo) con conteggio dei solleciti già inviati.",
          "Sollecita: invia un promemoria di pagamento al cliente (Email/SMS/WhatsApp).",
          "Finanziamento respinto: riapre il titolo (storno del sospeso) e avvisa il cliente di regolarizzare.",
        ]} />
        <Callout tipo="warn">L'invio di Email/SMS/WhatsApp è al momento <b>simulato</b> (nessun provider collegato). Verrà attivato collegando un servizio di invio.</Callout>
      </Scheda>

      <Scheda id="operativita" n="8" titolo="Operatività avanzata" accent={A}>
        <Bullets items={[
          "Bonifici: registrazione bonifici (anche a 2 conti) verso compagnie/collaboratori.",
          "Estratti conto: riepiloghi periodici dei movimenti.",
          "Subagenzia: gestione dei rapporti e saldi con la subagenzia.",
          "Quietanzamento / Decadi: gestione quietanze per compagnia e scadenze a decadi/quindicine.",
          "Assimoco: workflow dedicato alla compagnia Assimoco.",
        ]} />
      </Scheda>

      <Scheda id="riconciliazione" n="9" titolo="Riconciliazione bancaria" accent={A}>
        <P>Confronta i movimenti della <b>banca (file XLSX)</b> con quelli di <b>Assieasy (file CSV)</b> per verificare la quadratura.</P>
        <Steps items={[
          "Carica il file XLSX della banca e il CSV di Assieasy.",
          "Il motore propone gli abbinamenti automatici (per importo/data/riferimento).",
          "Verifichi/confermi gli abbinamenti; il badge di quadratura indica gli scostamenti.",
          "Esporti il risultato in PDF/Excel e archivi il lotto riconciliato.",
        ]} />
      </Scheda>

      <Scheda id="faq" n="10" titolo="FAQ e avvertenze" accent={A}>
        <FAQ items={[
          { q: "Ho incassato con la modalità sbagliata, come rimedio?", a: "Apri il titolo (stato Incassato) e premi Storna: torna Da incassare e potrai reincassarlo con la modalità corretta." },
          { q: "Perché un titolo risulta “Anticipato” e non “Incassato”?", a: "Perché è stato pagato tramite anticipo (cliente o collaboratore): il denaro non è ancora in cassa. Lo regolarizzi dalla scheda Crediti → Recupera." },
          { q: "La chiusura non quadra: cosa faccio?", a: "Inserisci l'importo realmente contato e compila la Nota di Rettifica obbligatoria; la chiusura verrà comunque registrata e tracciata." },
          { q: "Gli assegni incassati dove finiscono?", a: "Confluiscono nella Prima Nota Cassa (modalità Assegno) e rientrano nel “Da versare”. La distinta di versamento (in Prima Nota) li separa dai contanti per la banca." },
          { q: "Perché l'incasso di un titolo compare anche nel Foglio Cassa e in Prima Nota?", a: "È la catena unica: l'incasso crea un solo movimento nel Foglio Cassa del produttore e confluisce in Prima Nota. Non c'è doppio conteggio (la Contabilità non registra più due volte la cassa fisica)." },
          { q: "Nella riconciliazione compagnie ho tantissimi ERRORE, è normale?", a: "Confronta un periodo alla volta: il periodo è precompilato con le date del foglio caricato. Se carichi un foglio parziale, gli incassi fuori da quel periodo non vengono confrontati, evitando falsi errori." },
          { q: "Come autorizzo un collega a importare i titoli?", a: "Dalla Direzione, in Portafoglio titoli, premi “Autorizza import” e aggiungi la sua email." },
          { q: "I solleciti vengono davvero inviati?", a: "Al momento l'invio è simulato. Il testo e il flusso funzionano; l'invio reale sarà attivo dopo il collegamento di un provider Email/SMS." },
        ]} />
      </Scheda>
    </ManualeLayout>
  );
}
